import { useState, useEffect, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

interface MaintenanceGuardProps {
  children: ReactNode;
}

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const checkApiConnection = async () => {
    setIsChecking(true);
    try {
      await api.health.check();
      setIsOnline(true);
    } catch {
      setIsOnline(false);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkApiConnection();

    // Re-check every 30 seconds if offline
    const interval = setInterval(() => {
      if (!isOnline) {
        checkApiConnection();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isOnline]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    checkApiConnection();
  };

  if (isChecking && retryCount === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent mx-auto" />
          <p className="mt-4 text-sm text-gray-600">Connecting to server...</p>
        </div>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-gray-900">
            Service Temporarily Unavailable
          </h1>
          <p className="mt-3 text-gray-600">
            We're currently performing maintenance or experiencing technical difficulties.
            Please try again in a few moments.
          </p>
          <div className="mt-8">
            <button
              onClick={handleRetry}
              disabled={isChecking}
              className="btn-primary inline-flex items-center"
            >
              {isChecking ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </>
              )}
            </button>
          </div>
          <p className="mt-6 text-xs text-gray-400">
            If this problem persists, please contact support.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
